import { ApiClientConfig } from "./api-client-config";
import { EditorConfig } from "./editor-config";
import { GetUserTokenRequest, GetUserTokenShopifyAdapterRequest } from "./get-user-token-request";
import { RawProject } from "./raw-project";
import { UrlHelper } from "./url-helper";
import { showUserTokenAlert } from "./user-token-alert";

const USER_TOKEN_MAX_ATTEMPTS = 3;
const USER_TOKEN_RETRY_DELAY = 500;

export class ApiClient {

    public constructor(private _apiClientConfig: ApiClientConfig) { };

    /**
     * Returns an information about the tenant applications.
     * @param tenantId 
     */
    public async getTenantApplications() {
        try {
            const url = new URL(this._apiClientConfig.backOfficeUrl);
            url.pathname = `/api/storefront/v1/tenant-info/applications`;
            url.searchParams.set('tenantId', this._apiClientConfig.tenantId);

            return await fetch(url, {
                method: 'GET'
            }).then((res) => res.json());
        } catch (e) {
            console.error(e);
        }
    }

    public async getIntegrationInfo(shopifyProductId: string) {
        try {
            const apiUrl = this.buildGetIntegrationInfoUrl(shopifyProductId);

            console.log('GetIntegrationBoUrl is :', apiUrl.href);

            const response = await fetch(apiUrl, {
                method: 'GET'
            });
            const data = await this.handleJsonResponse(response);
    
            return data.result;
        }
        catch (e) {
            console.error(e);
        }
    }

    public async getProductVariantIdBySku(sku: string) {
        try {
            const apiUrl = this.buildGetProductVariantIdBySku(sku);

            const response = await fetch(apiUrl, {
                method: 'GET'
            });
            
            return await this.handleJsonResponse(response);    
        }
        catch (e) {
            console.error(e);
        }
    }

    public async postRawProject(rawProject: RawProject) {
        try {
            const apiUrl = this.buildPostTemporaryProjectUrl();

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(rawProject)
            });
    
            return this.handleJsonResponse(response);
        }
        catch (e) {
            console.error(e);
        }
    }

    public async getPimProduct(productId: number, productVersionId: number, userToken: string) {
        try{
            const apiUrl = this.buildGetPimProductUrl(productId, productVersionId);
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: userToken ? { 'Authorization': 'Bearer ' + userToken } : {},
            });

            const data = await this.handleJsonResponse(response);

            return data;
        }
        catch (e) {
            console.error(e);
        }
    }

    /**
     * Returns a user token. Transient failures (network errors, 429, 5xx) are retried.
     * If the token could not be obtained, the customer is notified with an alert and an error
     * is thrown, so the editor initialization in theme snippets stops instead of sending
     * requests without a token.
     */
    public async getUserToken(): Promise<string> {
        let lastError: unknown = null;

        const apiUrl = new URL(this.buildGetUserTokenUrl());
        const requestBody = this.buildGetUserTokenRequest();

        apiUrl.searchParams.set('tenantId', requestBody.tenantId);
        apiUrl.searchParams.set('storefrontId', requestBody.storefrontId);
        apiUrl.searchParams.set('storefrontUserId', requestBody.storefrontUserId);

        for (let attempt = 1; attempt <= USER_TOKEN_MAX_ATTEMPTS; attempt++) {
            let isTransient = true;

            try {
                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: { 'Content-Type': 'text/plain' },
                    // ! headers: { 'Content-Type': 'application/json' },
                });

                if (!response.ok) {
                    isTransient = response.status === 429 || response.status >= 500;
                    throw new Error(`HTTP ${response.status} ${response.statusText}`);
                }

                const token = this.normalizeToken(await response.text());

                if (!this.isJwt(token)) {
                    throw new Error('Response does not contain a valid token');
                }

                return token;
            }
            catch (e) {
                lastError = e;
                console.error(`[ApiClient] Failed to get user token for "${requestBody.storefrontUserId}" (attempt ${attempt}/${USER_TOKEN_MAX_ATTEMPTS})`, e);

                if (!isTransient || attempt === USER_TOKEN_MAX_ATTEMPTS) {
                    break;
                }

                await new Promise((resolve) => setTimeout(resolve, USER_TOKEN_RETRY_DELAY * attempt));
            }
        }

        const reason = lastError instanceof Error ? lastError.message : String(lastError);
        showUserTokenAlert(reason);

        throw new Error(`[ApiClient] Editor is not initialized: failed to get user token for "${requestBody.storefrontUserId}". ${reason}`);
    }

    private normalizeToken(token: string) {
        // remove escaped symbols ( sample: /" )
        let arr = Array.from(token.replace(/^\\"(.*)\\"$/, '$1'));
        if (arr[0] === '\"' || arr[0] === '"') {
            arr.shift();
        }
        if (arr[arr.length-1] === '\"' || arr[arr.length-1] === '"') {
            arr.pop();
        }
        return arr.join('').trim();
    }

    private isJwt(token: string) {
        return /^[\w-]+\.[\w-]+\.[\w-]*$/.test(token);
    }

    private buildPostTemporaryProjectUrl() {
        const url = UrlHelper.urlCombine(
            this._apiClientConfig.adapterBaseUrl,
            'api/shopify-adapter/v1/raw-projects'
        );
        url.searchParams.append('tenantId', this._apiClientConfig.tenantId);

        return url;
    }

    private buildGetIntegrationInfoUrl(shopifyProductId: string) {
        const url = UrlHelper.urlCombine(
            this._apiClientConfig.backOfficeUrl,
            'api/v1/tenants',
            this._apiClientConfig.tenantId,
            'integrations',
            shopifyProductId);

        url.searchParams.append('storefrontId', this._apiClientConfig.ecommerceSystemId);

        return url;
    }

    private buildGetProductVariantIdBySku(sku: string) {
        const url = UrlHelper.urlCombine(
            this._apiClientConfig.adapterBaseUrl,
            'api/shopify-adapter/v1/products/get-variant-id-by-sku');

        url.searchParams.append('tenantId', this._apiClientConfig.tenantId);
        url.searchParams.append('storefrontId', this._apiClientConfig.ecommerceSystemId);
        url.searchParams.append('sku', sku);

        return url;
    }

    private buildGetPimProductUrl(productId: number, productVersionId: number) {
        const url = UrlHelper.urlCombine(
            this._apiClientConfig.backOfficeUrl,
            '/api/storefront/v1/products/',
            productId.toString());

        url.searchParams.append('productVersionId', productVersionId.toString());
        url.searchParams.append('tenantId', this._apiClientConfig.tenantId);

        return url;
    }

    // private buildGetUserTokenUrl() {
    //     const url = UrlHelper.urlCombine(
    //         this._apiClientConfig.backOfficeUrl,
    //         '/api/v1/tenants/',
    //         this._apiClientConfig.tenantId,
    //         '/integrations/getusertoken'
    // );

    //     return url;
    // }

    private buildGetUserTokenUrl() {
        const url = UrlHelper.urlCombine(
            this._apiClientConfig.adapterBaseUrl,
            '/api/shopify-adapter/v1/tenants/',
            this._apiClientConfig.tenantId,
            'user-token'
        );

        return url;
    }

    private buildGetUserTokenRequest() : GetUserTokenShopifyAdapterRequest {
        // const request : GetUserTokenRequest = {
        //     ecommerceDomain: (window as any).location.origin,
        //     origin: (window as any).location.origin,
        //     storefrontId: this._apiClientConfig.ecommerceSystemId,
        //     userGuid: this._apiClientConfig.userId
        // }
        const request : GetUserTokenShopifyAdapterRequest = {
            storefrontId: this._apiClientConfig.ecommerceSystemId,
            tenantId: this._apiClientConfig.tenantId,
            storefrontUserId: this._apiClientConfig.userId
        }
        return request;
    }

    private handleJsonResponse(response: Response) {
        if (!response.ok) {
            throw Error(response.statusText);
        }
        return response.json();
    }
}